import {
  Accordion,
  AccordionItem,
  Button,
  IconButton,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { SubtractAlt } from '@carbon/react/icons';
import {
  SurveySection,
  describeSurveyAreaCandidate,
} from 'components/survey-description';
import { UploadSection } from 'components/create-area-panel';
import type { AreaGeometry } from 'types/area-geometry';
import {
  REFERENCE_LAYERS_TITLE,
  ReferenceLayersHeading,
} from './reference-layers-heading';
import { ReferenceLayersSection } from './reference-layers-section';
import type { EditMethod, EditorSession } from './types';
import {
  EDITOR_SURVEY_HINT,
  EDITOR_UPLOAD_HINT,
  EDIT_METHODS,
  partRows,
} from './utils';
import styles from './area-geometry-editor.module.scss';

interface EditMethodRailProps {
  area: AreaGeometry;
  session: EditorSession;
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
}

/**
 * Upload shape file's inputs: the files, and why they can't be added when the
 * preview refuses them.
 *
 * @param {object} props - The props.
 * @param {EditorSession} props.session - The editing session.
 * @return {JSX.Element} The block.
 */
function UploadMethod({ session }: { session: EditorSession }): JSX.Element {
  const { upload, addUploadFiles, removeUploadFile, addedErrors } = session;
  return (
    <div className={styles.subMethodGroup}>
      <span className={styles.subMethodLabel}>Shape file</span>
      {upload.result && addedErrors.length > 0 && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={
            upload.items.length > 1
              ? 'Could not add these files'
              : 'Could not add this file'
          }
          subtitle={addedErrors.join(' ')}
        />
      )}
      <div className={styles.subMethodPanel}>
        <UploadSection
          upload={upload}
          onAdd={addUploadFiles}
          onRemove={removeUploadFile}
          hint={EDITOR_UPLOAD_HINT}
          kept="added"
        />
      </div>
    </div>
  );
}

/**
 * The tools column beside the map: a save refusal, then Edit Method — the three
 * methods, the chosen method's inputs, and "Areas in this geometry" with a remove
 * per part and Remove original area — then Reference Area Layers. Cancel and Save
 * dock under it, so the map runs to the bottom edge.
 *
 * @param {EditMethodRailProps} props - The area, the session and the two exits.
 * @return {JSX.Element} The rail.
 */
export function EditMethodRail({
  area,
  session,
  canSave,
  onCancel,
  onSave,
}: EditMethodRailProps): JSX.Element {
  const {
    method,
    setMethod,
    surveyRows,
    setSurveyRows,
    surveyPick,
    parts,
    partFigures,
    addedErrors,
    removePart,
    canRemoveOriginal,
    removeOriginal,
    draft,
  } = session;

  return (
    <aside className={styles.toolsPanel} aria-label="Geometry edit tools">
      <div className={styles.toolsScroll}>
        {draft.errors.length > 0 && (
          <InlineNotification
            className={styles.saveError}
            kind="error"
            lowContrast
            title="Could not save the geometry"
            subtitle={draft.errors.join(' ')}
            onCloseButtonClick={draft.reset}
          />
        )}
        <Accordion>
          <AccordionItem title="Edit Method" open>
            <div className={styles.optionStack}>
              <RadioButtonGroup
                name="edit-method"
                legendText=""
                aria-label="Edit method"
                orientation="vertical"
                valueSelected={method}
                onChange={(value: string | number | undefined) =>
                  setMethod(String(value) as EditMethod)
                }
              >
                {EDIT_METHODS.map(({ id, title, description }) => (
                  <RadioButton
                    key={id}
                    id={`edit-method-${id}`}
                    value={id}
                    labelText={
                      <span className={styles.radioLabel}>
                        <span>{title}</span>
                        <small>{description}</small>
                      </span>
                    }
                  />
                ))}
              </RadioButtonGroup>

              {method === 'survey-description' && (
                <div className={styles.subMethodGroup}>
                  <span className={styles.subMethodLabel}>
                    Survey description
                  </span>
                  {surveyPick.notice && (
                    <InlineNotification
                      kind="error"
                      lowContrast
                      title="Could not add that section"
                      subtitle={surveyPick.notice}
                      onCloseButtonClick={surveyPick.dismiss}
                    />
                  )}
                  {/* An overlap the map can't decide is chosen here, by name, as
                      the Create New Area panel does. */}
                  {surveyPick.choices.length > 0 && (
                    <>
                      <InlineNotification
                        kind="info"
                        lowContrast
                        title="More than one survey area here"
                        subtitle="Pick the one you meant:"
                        onCloseButtonClick={surveyPick.dismiss}
                      />
                      <span className={styles.pickChoices}>
                        {surveyPick.choices.map((candidate) => (
                          <Button
                            key={candidate.id}
                            kind="ghost"
                            size="sm"
                            onClick={() => surveyPick.choose(candidate)}
                          >
                            {describeSurveyAreaCandidate(candidate)}
                          </Button>
                        ))}
                      </span>
                    </>
                  )}
                  {addedErrors.length > 0 && (
                    <InlineNotification
                      kind="error"
                      lowContrast
                      hideCloseButton
                      title="Could not add these survey areas"
                      subtitle={addedErrors.join(' ')}
                    />
                  )}
                  <div className={styles.subMethodPanel}>
                    <SurveySection
                      rows={surveyRows}
                      onChange={setSurveyRows}
                      title=""
                      hint={EDITOR_SURVEY_HINT}
                      idPrefix="edit-survey"
                      condensed
                    />
                  </div>
                </div>
              )}

              {method === 'upload' && <UploadMethod session={session} />}

              <div className={styles.surveyStack}>
                <span className={styles.sectionLabel}>
                  Areas in this geometry
                </span>
                {partRows(parts, partFigures).map((part) => (
                  <div key={part.index} className={styles.partRow}>
                    <span className={styles.partLabel}>
                      Polygon {part.index + 1} · {part.label}
                    </span>
                    <IconButton
                      label={`Remove polygon ${part.index + 1}`}
                      align="left"
                      kind="ghost"
                      size="sm"
                      disabled={parts.length <= 1 || draft.saving}
                      onClick={() => removePart(part.index)}
                    >
                      <SubtractAlt />
                    </IconButton>
                  </div>
                ))}
                <Button
                  kind="danger--ghost"
                  size="sm"
                  disabled={!canRemoveOriginal || draft.saving}
                  onClick={removeOriginal}
                >
                  Remove original area
                </Button>
              </div>
            </div>
          </AccordionItem>
          <AccordionItem
            title={REFERENCE_LAYERS_TITLE}
            renderToggle={ReferenceLayersHeading}
            open
            className={styles.refAreasItem}
          >
            <ReferenceLayersSection area={area} session={session} />
          </AccordionItem>
        </Accordion>
      </div>
      <div className={styles.toolsFooter}>
        <Button kind="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          kind="primary"
          size="md"
          disabled={!canSave || draft.saving}
          onClick={onSave}
        >
          {draft.saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </aside>
  );
}
